import { traverse, type ExprNode } from '@learnforge/demo-math/expr';
import { findZerosBisection } from './zeros';
import { sampleCurve } from './evaluate';

export interface Interval {
  start: number;
  end: number;
  openLeft: boolean;
  openRight: boolean;
}

export interface ExcludedPoint {
  x: number;
  reason: 'division' | 'log' | 'sqrt' | 'tan';
}

export interface Domain {
  intervals: Interval[];
  excluded: ExcludedPoint[];
}

function findDivisionExclusions(node: ExprNode): number[] {
  const exclusions: number[] = [];
  traverse(node, (sub) => {
    if (sub.kind === 'pow' && sub.exp.kind === 'num' && sub.exp.value === -1) {
      const denom = sub.base;
      const zeros = findZerosBisection(denom, -1000, 1000);
      for (const z of zeros) exclusions.push(z);
    }
  });
  return exclusions;
}

function findRequirePositive(node: ExprNode): { node: ExprNode; closed: boolean } | null {
  let result: { node: ExprNode; closed: boolean } | null = null;
  traverse(node, (sub) => {
    if (sub.kind === 'func' && sub.args.length === 1) {
      if (sub.name === 'sqrt') result = { node: sub.args[0]!, closed: true };
      else if (sub.name === 'log' || sub.name === 'ln')
        result = { node: sub.args[0]!, closed: false };
    }
  });
  return result;
}

export function computeNaturalDomain(node: ExprNode): Domain {
  const exclusions = findDivisionExclusions(node);
  const positivity = findRequirePositive(node);

  if (!positivity) {
    return {
      intervals: [{ start: -Infinity, end: Infinity, openLeft: true, openRight: true }],
      excluded: exclusions.map((x) => ({ x, reason: 'division' as const })),
    };
  }

  const zeros = findZerosBisection(positivity.node, -1000, 1000);
  zeros.sort((a, b) => a - b);

  const samples = sampleCurve(positivity.node, -1000, 1000, 4000);
  const intervals: Interval[] = [];
  let inRegion = false;
  let regionStart = 0;
  for (let i = 0; i < samples.length; i++) {
    const x = -1000 + (2000 * i) / (samples.length - 1);
    const y = samples[i] as number;
    const ok = Number.isFinite(y) && (positivity.closed ? y >= 0 : y > 0);
    if (ok && !inRegion) {
      regionStart = x;
      inRegion = true;
    } else if (!ok && inRegion) {
      const prevX = -1000 + (2000 * (i - 1)) / (samples.length - 1);
      intervals.push({
        start: regionStart,
        end: prevX,
        openLeft: !positivity.closed,
        openRight: true,
      });
      inRegion = false;
    }
  }
  if (inRegion) {
    intervals.push({
      start: regionStart,
      end: Infinity,
      openLeft: !positivity.closed,
      openRight: true,
    });
  }

  if (intervals.length > 0 && zeros.length > 0) {
    const firstZero = zeros[0] as number;
    if (
      (intervals[0] as Interval).start > firstZero - 1 &&
      (intervals[0] as Interval).start < firstZero + 1
    ) {
      (intervals[0] as Interval).start = firstZero;
    }
  }

  if (intervals.length > 0 && (intervals[0] as Interval).start <= -999) {
    (intervals[0] as Interval).start = -Infinity;
  }

  return {
    intervals,
    excluded: exclusions.map((x) => ({ x, reason: 'division' as const })),
  };
}

export function rangeOverDomain(node: ExprNode, domain: Domain, samples: number = 400): Domain {
  const ys: number[] = [];
  for (const interval of domain.intervals) {
    const lo = Number.isFinite(interval.start) ? interval.start : -100;
    const hi = Number.isFinite(interval.end) ? interval.end : 100;
    const buf = sampleCurve(node, lo, hi, samples);
    for (const y of buf) if (Number.isFinite(y)) ys.push(y);
  }
  if (ys.length === 0) return { intervals: [], excluded: [] };
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  return {
    intervals: [{ start: yMin, end: yMax, openLeft: false, openRight: false }],
    excluded: [],
  };
}
